'use client'

import React, { useEffect, useRef, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import type { AppDispatch, RootState } from '@/store'
import { changeNonMemberModalState } from '@/store/modal/slice'
import Logo from '@/components/Shared/Logo'
import LogoBtn from '@/components/Shared/LogoBtn'
import { LOCAL_STORAGE_KEYS } from '@/constants/storageKeys'
import '@/styles/modal.scss'
import styles from './styles.module.scss'

export default function NonMemberModal() {
  const dispatch = useDispatch<AppDispatch>()
  const pathname = usePathname()
  const modal = useSelector((state: RootState) => state.modal.nonMemberModalState)
  const previousPathnameRef = useRef(pathname)
  const [isClosing, setIsClosing] = useState(false)

  const setStorage = useCallback((): void => {
    try {
      const path = window.location.pathname + window.location.search
      localStorage.setItem(LOCAL_STORAGE_KEYS.BEFORE_PATH, path)
    } catch (error) {
      if (process.env.NODE_ENV !== 'production')
        console.error('NonMemberModal: Error setting storage:', error)
    }
  }, [])

  useEffect(() => {
    if (modal) {
      setStorage()
    }
  }, [modal, setStorage])

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      if (modal) {
        dispatch(changeNonMemberModalState(false))
      }
      previousPathnameRef.current = pathname
    }
  }, [pathname, dispatch, modal])

  const modalClose = useCallback((): void => {
    setIsClosing(true)
    setTimeout(() => {
      dispatch(changeNonMemberModalState(false))
      setIsClosing(false)
    }, 300) // アニメーション時間と同じ
  }, [dispatch])

  const nonMemberModalClose = useCallback((): void => {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.BEFORE_PATH)
    } catch (error) {
      if (process.env.NODE_ENV !== 'production')
        console.error('NonMemberModal: Error clearing stored paths:', error)
    }
    modalClose()
  }, [modalClose])

  const overlayCloseModal = useCallback((): void => {
    nonMemberModalClose()
  }, [nonMemberModalClose])

  if (!modal) return null

  return (
    <div className={clsx('modalOverlay', { closing: isClosing })} onClick={overlayCloseModal}>
      <div className={styles.modalWindowWrapper}>
        <div
          className={clsx(styles.modalWindow, { [styles.closing]: isClosing })}
          onClick={(e) => e.stopPropagation()}
        >
          <button className={styles.buttonClose} onClick={nonMemberModalClose} />
          <div className={styles.modalContent}>
            <div className={styles.nonMemberContents}>
              <div className={styles.nonMemberModalLog}>
                <Logo />
              </div>
              <h2 className={styles.nonMemberModalTitle}>
                会員登録して、
                <br />
                最新の情報を手に入れよう
              </h2>
              <div className={clsx('buttonWrapper flexCenter', styles.nonMemberModalButtons)}>
                <p className={styles.buttonAreaLead}>
                  会員登録すると新着コンテンツのお知らせ機能や、お気に入りコンテンツの管理機能などを利用できます。
                </p>
                <LogoBtn text='メールで会員登録' type='email' className='email' />
                <LogoBtn type='google' text='Googleで会員登録' />
                <LogoBtn text='Xで会員登録' type='twitter' />
                <p className={styles.borderTitleTransparent}>会員の方はこちら</p>
                <LogoBtn
                  text='タレントブック・ストーリーにログイン'
                  type='talentbook'
                  link='/login'
                  className='talentbook'
                />
              </div>
              <p
                className={styles.closeModalButton}
                onClick={nonMemberModalClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    nonMemberModalClose()
                  }
                }}
                role='button'
                tabIndex={0}
              >
                閉じる
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
